import type { S } from "@/shared/api/client";

/** What a white-label workspace shows in place of ClientOS (null when it isn't one). */
export type Brand = S["Brand"];

const shades = ["--color-ledger", "--color-ledger-dark", "--color-ledger-soft"];

/**
 * Puts a white-label workspace's look on the page: its color in place of ClientOS green, its
 * name in the tab and its logo as the tab's icon. Without a brand, ClientOS's comes back.
 */
export function applyBrand(brand: Brand | null | undefined) {
  const root = document.documentElement.style;
  if (brand?.accent) {
    root.setProperty(shades[0], brand.accent);
    root.setProperty(shades[1], `color-mix(in srgb, ${brand.accent} 78%, black)`);
    root.setProperty(shades[2], `color-mix(in srgb, ${brand.accent} 12%, white)`);
  } else {
    for (const s of shades) root.removeProperty(s);
  }
  document.querySelectorAll<HTMLMetaElement>("meta[name='theme-color']").forEach((m) => {
    m.dataset.shipped ??= m.content;
    m.content = brand?.accent ?? m.dataset.shipped;
  });
  document.querySelectorAll<HTMLLinkElement>("link[rel~='icon'], link[rel='apple-touch-icon']").forEach((l) => {
    l.dataset.shipped ??= l.getAttribute("href") ?? "";
    l.setAttribute("href", brand?.logo ?? l.dataset.shipped);
  });
  document.querySelectorAll<HTMLMetaElement>("meta[name='apple-mobile-web-app-title']").forEach((m) => {
    m.dataset.shipped ??= m.content;
    m.content = brand?.name ?? m.dataset.shipped;
  });
  const shipped = (document.documentElement.dataset.title ??= document.title);
  document.title = brand ? brand.name : shipped;
}

/** White writing on the color, and the color as writing on white, read easily (WCAG AA, 4.5:1). */
export function readable(hex: string): boolean {
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return false;
  const lin = (i: number) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const l = 0.2126 * lin(1) + 0.7152 * lin(3) + 0.0722 * lin(5);
  return 1.05 / (l + 0.05) >= 4.5;
}
